/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/consistent-type-imports */
import {
  FinishNodeState,
  NodeStateStatus,
  NodeType,
  RenderNode,
  RenderNodeState,
  SchemaData,
  SerializedHistory,
  truncateSerializedSessionHistory,
} from '@lemonade-hq/chat-engine';
import { devWarn, Milliseconds } from '@lemonade-hq/ts-helpers';
import { useCallback, useRef } from 'react';
import { CURSOR_BLINK_ANIMATION_DURATION_MS, TEXT_ANIMATION_DURATION_MS } from '../utils/persistenceUtils';
import { ChatEngineSession } from './chat_engine.client';
import {
  convertAnswersToMessage,
  fromStartConversationResponse,
  updateSessionWithNewMessages,
} from './conversation_router.adapter';
import { ConversationRouterClient, StartConversationRequest } from './conversation_router.client';
import { UseChatServerPersistenceProps, UseChatServerPersistenceReturn } from './usePersistenceHooksTypes';
import type {
  WizardPersistenceContextSubmissionOptions,
  WizardPersistenceLoadSessionProps,
} from './wizard_persistence.context';

export const freeTextAttribute = `__FREE_TEXT__`;

const WAITING_TEXTS = ['Analyzing...', 'Checking...', 'Processing...', 'Thinking...'];

const getTransitionStep = <TPresentation = undefined>(textIndex: number): RenderNodeState<TPresentation> => {
  const isFirstStep = textIndex === 0;
  const index = (textIndex - 1) % WAITING_TEXTS.length;
  const text = isFirstStep ? '' : WAITING_TEXTS[index];

  return {
    id: 'tempId',
    name: 'tempName',
    renderedStep: {
      presentation: {
        frame: {
          avatar: 'hidden',
          title: isFirstStep ? text : { text, mode: 'reasoning' },
          freeTextInput: 'open',
        },
      },
    } as unknown,
    status: NodeStateStatus.Pending,
    type: NodeType.Render,
    answers: {},
  } as RenderNodeState<TPresentation>;
};

const getLastStepWithFreeTextAnswer = <TPresentation = undefined>(
  session: ChatEngineSession<TPresentation>,
  currentStepIndex: number,
  freeTextAnswer: string,
): RenderNodeState<TPresentation> => {
  const originalLastStep = { ...session.history[currentStepIndex] };
  return {
    ...originalLastStep,
    renderedStep: {
      ...originalLastStep.renderedStep,
      data: {
        ...originalLastStep.renderedStep.data,
        output: freeTextAnswer,
      },
    },
  };
};

const createWaitingSession = <TPresentation = undefined>(
  freeTextAnswer: string,
  session: ChatEngineSession<TPresentation>,
  currentStepIndex: number,
  textIndex: number,
): ChatEngineSession<TPresentation> => {
  const allSteps = session.history.slice(0, currentStepIndex);
  const lastStepWithFreeTextAnswer = getLastStepWithFreeTextAnswer<TPresentation>(
    session,
    currentStepIndex,
    freeTextAnswer,
  );
  const transitionStep = getTransitionStep<TPresentation>(textIndex);

  return {
    ...session,
    history: [...allSteps, lastStepWithFreeTextAnswer, transitionStep],
  } as ChatEngineSession<TPresentation>;
};

interface UseConversationRouterPersistenceProps<TPresentation = undefined>
  extends UseChatServerPersistenceProps<TPresentation> {
  readonly session: ChatEngineSession<TPresentation> | null;
  readonly currentStepIndex: number;
}

export const useConversationRouterPersistence = <TPresentation = undefined>({
  scriptName,
  serverTimeoutOverride,
  redirectFn,
  setSession,
  setUiState,
  sessionId,
  currentStepId,
  currentStepIndex,
  abortController,
  session,
}: UseConversationRouterPersistenceProps<TPresentation>): UseChatServerPersistenceReturn<TPresentation> => {
  const conversationRouterClient = useRef<ConversationRouterClient>();
  const sessionTimeoutOverrideRef = useRef<Milliseconds | undefined>(serverTimeoutOverride);
  const llmResponseTimeoutRef = useRef<NodeJS.Timeout>();

  const initSession = useCallback(
    async ({
      initialStepName,
      serverBaseUrl,
      accessTokenOverride,
      emailCode,
      clearPreviousSession = false,
      ...loadSessionProps
    }: WizardPersistenceLoadSessionProps) => {
      if (clearPreviousSession) {
        setSession(null);
      }

      conversationRouterClient.current = new ConversationRouterClient({
        serverBaseUrl,
        accessTokenOverride,
        emailCode,
      });
      sessionTimeoutOverrideRef.current = serverTimeoutOverride;

      setUiState(val => {
        return { ...val, isLoadingInitialServerData: true };
      });

      const startRequest: StartConversationRequest = {
        experience: scriptName,
        channel: 'web',
        context: loadSessionProps.initData,
        forceRestart: loadSessionProps.forceRestart,
      };

      const fetchedSession = await conversationRouterClient.current.startConversation(startRequest);

      // Convert the conversation router response to chat engine format
      const convertedSession = fromStartConversationResponse<TPresentation>(fetchedSession, scriptName);

      if (convertedSession.history.length === 1) {
        const firstNode = convertedSession.history[0] as unknown as
          | FinishNodeState<{ redirectTo?: string }>
          | RenderNode;

        if (firstNode.type === NodeType.Finish && firstNode.payload.redirectTo != null) {
          await redirectFn?.(firstNode.payload.redirectTo);
        }
      }

      if (initialStepName != null) {
        const index = convertedSession.history.findIndex(step => step.name === initialStepName);
        /* istanbul ignore if -- @preserve - missing test */
        if (index > -1) {
          const truncatedSession = truncateSerializedSessionHistory({
            session: convertedSession,
            answeredNodeIndexInHistory: convertedSession.history.findIndex(step => step.name === initialStepName),
          });
          setSession(truncatedSession);
        } else {
          setSession(convertedSession);
        }
      } else {
        setSession(convertedSession);
      }

      setUiState(val => {
        return { ...val, isLoadingInitialServerData: false };
      });
    },
    [redirectFn, scriptName, serverTimeoutOverride, setSession, setUiState],
  );

  const handleFreeTextWaitingUI = useCallback(
    (
      freeTextAnswer: string,
      sessionToUpdate: ChatEngineSession<TPresentation>,
      lastStepIndex: number,
      textIndex = 0,
    ): ChatEngineSession<TPresentation> => {
      const updatedSession = createWaitingSession<TPresentation>(
        freeTextAnswer,
        sessionToUpdate,
        lastStepIndex,
        textIndex,
      );

      const delay = textIndex === 0 ? CURSOR_BLINK_ANIMATION_DURATION_MS : TEXT_ANIMATION_DURATION_MS;

      setSession(updatedSession);

      llmResponseTimeoutRef.current = setTimeout(() => {
        handleFreeTextWaitingUI(freeTextAnswer, sessionToUpdate, lastStepIndex, textIndex + 1);
      }, delay);

      return updatedSession;
    },
    [setSession],
  );

  const submitAnswersToServer = useCallback(
    async (
      answers: SchemaData,
      _options?: WizardPersistenceContextSubmissionOptions,
      onSubmitError?: (error: unknown) => void,
    ) => {
      return await new Promise<{ history: SerializedHistory<TPresentation>; success: boolean }>((resolve, reject) => {
        async function submitAnswers() {
          const isFreeText = freeTextAttribute in answers;

          /* istanbul ignore if -- @preserve - conversation router is defined at init session */
          if (conversationRouterClient.current == null) {
            devWarn('Conversation router client not initialized');
            resolve({ history: [] as SerializedHistory<TPresentation>, success: false });

            return;
          }

          if (session == null || sessionId == null || currentStepId == null) {
            devWarn('Cannot submit without a session ID or current step ID');
            resolve({ history: [] as SerializedHistory<TPresentation>, success: false });

            return;
          }

          const freeTextAnswer = isFreeText ? (answers[freeTextAttribute] as string) : '';

          try {
            if (isFreeText) {
              const updatedSession = handleFreeTextWaitingUI(freeTextAnswer, session, currentStepIndex);

              setUiState(val => {
                return { ...val, isSubmittingToServer: false, isSubmittingLLMRequest: true };
              });
              resolve({ history: updatedSession.history, success: true });
            }

            abortController.current = new AbortController();

            const messageRequest = convertAnswersToMessage(answers, sessionId, currentStepId);

            const response = await conversationRouterClient.current.sendMessage(messageRequest);

            clearTimeout(llmResponseTimeoutRef.current);

            const updatedSession = updateSessionWithNewMessages<TPresentation>(session, response.messages);

            setSession(updatedSession);
            setUiState(val => {
              return { ...val, isSubmittingToServer: false, isSubmittingLLMRequest: false };
            });
            resolve({ history: updatedSession.history, success: true });
          } catch (error) {
            clearTimeout(llmResponseTimeoutRef.current);

            if (isFreeText) {
              const allSteps = session.history.slice(0, currentStepIndex + 1);

              setSession({ ...session, history: allSteps });
              setUiState(val => {
                return { ...val, isSubmittingLLMRequest: false, isErrorFromLLMRequest: true };
              });
              onSubmitError?.(error);
            } else {
              reject(error instanceof Error ? error : /* istanbul ignore next -- @preserve */ new Error(String(error)));
            }
          }
        }

        void submitAnswers();
      });
    },
    [
      currentStepId,
      sessionId,
      session,
      setSession,
      setUiState,
      abortController,
      handleFreeTextWaitingUI,
      currentStepIndex,
    ],
  );

  return {
    initSession,
    submitAnswersToServer,
  };
};
